<template>
  <v-container fluid>
    <h2>{{ $tc('root_cause_analysis') }}</h2>
    <div v-if="modelLoaded">
      <route-assessment-form
        :model="model"
        :route="model.route"
        :customer-assessment-terms="customerAssessmentTerms"
        :require-probed-temperatures="customerContext.getSetting('requireManualTempsInRootCauseAssessment')"
        show-route-metadata
        show-violation-summary
        show-temperature-graph
        v-on:saved="redirectToIndex" />
    </div>
  </v-container>
</template>

<script>
import RouteAssessmentForm from '@/components/root-cause/RouteAssessmentForm'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import BrowserUtilitiesMixin from '@/components/mixins/BrowserUtilitiesMixin'
import { isEmpty } from 'lodash'

export default {
  name: 'RootCauseView',
  components: {
    RouteAssessmentForm
  },
  mixins: [
    CurrentUserMixin,
    BrowserUtilitiesMixin
  ],
  data () {
    return {
      model: {},
      customerAssessmentTerms: []
    }
  },
  computed: {
    modelLoaded () {
      return this.model.id != null
    }
  },
  methods: {
    /**
     * The form saved, so return to the archive and report the outcome. Both banners are raised inside the push
     * callback because the router's beforeEach clears flash banners on navigation, so anything dispatched before
     * this point is gone by the time the index renders (VF-4395).
     *
     * @param {RouteAssessmentRecord} routeAssessment
     * @param {string|null} secondarySaveMessage set when the assessment saved but one of its follow-up writes
     * (its note, or a probed temperature) did not
     * @return undefined
     */
    redirectToIndex (routeAssessment, secondarySaveMessage = null) {
      this.$router.push('/root-cause/index', () => {
        if (secondarySaveMessage) {
          this.$store.dispatch('globalMessagingModule/showErrorFlashBanner', secondarySaveMessage)
          return
        }
        this.$store.dispatch(
          'globalMessagingModule/showSuccessFlashBanner',
          `${this.$i18n.tc('route_assessment')} ${this.$i18n.t('generic_save_success')}`
        )
      })
    },
    /**
     * Fetch the customer assessment terms used in the Root Cause Analysis forms
     */
    async fetchCustomerAssessmentTerms () {
      try {
        let response = await this.$orm('CustomerAssessmentTerm').findAll({
          customer_id: this.customerContext.id,
          category: 'route'
        })
        // If the customer didn't have any terms, try the parent customer.
        if (isEmpty(response.getItems()) && this.customerContext.parent_id != null) {
          response = await this.$orm('CustomerAssessmentTerm').findAll({
            customer_id: this.customerContext.parent_id,
            category: 'route'
          })
        }
        this.customerAssessmentTerms = response.getItems()
      } catch (error) {
        this.handleError(error)
      }
    }
  },
  created () {
    const expandParams = [
      'route.interpolated_sensor_data',
      'route.route_products.product',
      'route.route_violations.route_product.product',
      'route.vehicle',
      'notes.user_email',
      'route.customer.customer_settings',
      'route.route_locations.simulated_temperatures_at_arrival',
      'route.route_locations.route_product_recorded_temperatures',
      'route.route_locations.location'
    ]

    this.$orm('RouteAssessment')
      .find(this.$route.params.id, { params: { expand: expandParams.join(',') } })
      .then((RouteAssessment) => {
        this.model = RouteAssessment
        this.updateBrowserTitle(`${this.$tc('root_cause_analysis')} ${this.model.id}`)
      })
      .catch(error => {
        this.handleError(error)
      })
    this.fetchCustomerAssessmentTerms()
  }
}
</script>
